import { Box, Input, NativeSelect, Text, VStack } from '@chakra-ui/react'
import { memo, useMemo } from 'react'

import { useGetDeviceModels } from '../hooks/useDeviceModels'
import { useDevicesI18n } from '../i18n'
import type { DevicesListFilters as DevicesListFiltersType } from '../types/device.types'

export interface DevicesListFiltersProps {
  value: Partial<DevicesListFiltersType>
  onChange: (filters: Partial<DevicesListFiltersType>) => void
}

export const DevicesListFilters = memo<DevicesListFiltersProps>(
  ({ value, onChange }) => {
    const { text } = useDevicesI18n()
    const { data: modelsData } = useGetDeviceModels()
    const models = modelsData ?? []

    const TRANSPORT_OPTIONS = useMemo(
      () => [
        { value: '', label: text('devices.common.all', 'All') },
        { value: 'UDP', label: text('devices.options.transportUdp', 'UDP') },
        { value: 'TCP', label: text('devices.options.transportTcp', 'TCP') },
        { value: 'TLS', label: text('devices.options.transportTls', 'TLS') },
      ],
      [text],
    )

    const JITTER_OPTIONS = useMemo(
      () => [
        { value: '', label: text('devices.common.all', 'All') },
        {
          value: 'normal',
          label: text('devices.options.jitterNormal', 'Normal'),
        },
        { value: 'low', label: text('devices.options.jitterLow', 'Low') },
        { value: 'high', label: text('devices.options.jitterHigh', 'High') },
      ],
      [text],
    )

    const WARRANTY_OPTIONS = useMemo(
      () => [
        { value: '', label: text('devices.common.all', 'All') },
        {
          value: 'unknown',
          label: text('devices.filters.warrantyUnknown', 'No warranty'),
        },
        {
          value: 'in_warranty',
          label: text('devices.filters.warrantyInWarranty', 'In warranty'),
        },
        {
          value: 'expired',
          label: text('devices.filters.warrantyExpired', 'Expired'),
        },
      ],
      [text],
    )

    const SIDECAR_OPTIONS = useMemo(
      () => [
        { value: '', label: text('devices.common.all', 'All') },
        { value: 'true', label: text('devices.common.yes', 'Yes') },
        { value: 'false', label: text('devices.common.no', 'No') },
      ],
      [text],
    )

    const PROVISIONING_OPTIONS = useMemo(
      () => [
        { value: '', label: text('devices.common.all', 'All') },
        {
          value: 'enabled',
          label: text('devices.filters.provisioningEnabled', 'Enabled'),
        },
        {
          value: 'disabled',
          label: text('devices.filters.provisioningDisabled', 'Disabled'),
        },
        {
          value: 'changes_pending',
          label: text(
            'devices.filters.provisioningChangesPending',
            'Changes pending',
          ),
        },
      ],
      [text],
    )

    const manufacturers = useMemo(() => {
      const seen = new Set<number>()
      return models
        .filter(
          (m) => !seen.has(m.manufacturer_id) && seen.add(m.manufacturer_id),
        )
        .map((m) => ({ id: m.manufacturer_id, name: m.manufacturer_name }))
        .sort((a, b) => a.name.localeCompare(b.name))
    }, [models])

    const modelsForMake = useMemo(() => {
      if (value.manufacturer_id == null) return models
      return models.filter((m) => m.manufacturer_id === value.manufacturer_id)
    }, [models, value.manufacturer_id])

    const update = (updates: Partial<DevicesListFiltersType>) => {
      const next = { ...value, ...updates }
      if (
        updates.manufacturer_id !== undefined &&
        updates.device_model === undefined
      ) {
        next.device_model = undefined
      }
      const normalized: Partial<DevicesListFiltersType> = {}
      if (next.manufacturer_id !== undefined)
        normalized.manufacturer_id = Number(next.manufacturer_id)
      if (next.device_model !== undefined)
        normalized.device_model = Number(next.device_model)
      if (next.firmware_version?.trim())
        normalized.firmware_version = next.firmware_version.trim()
      if (next.warranty) normalized.warranty = next.warranty
      if (next.transport) normalized.transport = next.transport
      if (next.jitter) normalized.jitter = next.jitter
      if (next.sidecar_enabled !== undefined)
        normalized.sidecar_enabled = next.sidecar_enabled
      if (next.provisioning) normalized.provisioning = next.provisioning
      onChange(normalized)
    }

    return (
      <VStack gap={4} align="stretch">
        <Box>
          <Text fontSize="xs" color="fg.muted" mb={1}>
            {text('devices.filters.make', 'Make')}
          </Text>
          <NativeSelect.Root size="sm" w="full">
            <NativeSelect.Field
              value={value.manufacturer_id ?? ''}
              onChange={(e) => {
                const v = e.target.value
                update({
                  manufacturer_id: v ? Number(v) : undefined,
                  device_model: undefined,
                })
              }}
            >
              <option value="">{text('devices.common.all', 'All')}</option>
              {manufacturers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Box>
        <Box>
          <Text fontSize="xs" color="fg.muted" mb={1}>
            {text('devices.filters.model', 'Model')}
          </Text>
          <NativeSelect.Root size="sm" w="full">
            <NativeSelect.Field
              value={value.device_model ?? ''}
              onChange={(e) => {
                const v = e.target.value
                update({ device_model: v ? Number(v) : undefined })
              }}
            >
              <option value="">{text('devices.common.all', 'All')}</option>
              {modelsForMake.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.manufacturer_name} {m.name}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Box>
        <Box>
          <Text fontSize="xs" color="fg.muted" mb={1}>
            {text('devices.filters.firmware', 'Firmware')}
          </Text>
          <Input
            size="sm"
            w="full"
            placeholder={text('devices.filters.firmwarePlaceholder', 'Any')}
            value={value.firmware_version ?? ''}
            onChange={(e) =>
              update({ firmware_version: e.target.value || undefined })
            }
          />
        </Box>
        <Box>
          <Text fontSize="xs" color="fg.muted" mb={1}>
            {text('devices.filters.warranty', 'Warranty')}
          </Text>
          <NativeSelect.Root size="sm" w="full">
            <NativeSelect.Field
              value={value.warranty ?? ''}
              onChange={(e) =>
                update({
                  warranty: (e.target.value ||
                    undefined) as DevicesListFiltersType['warranty'],
                })
              }
            >
              {WARRANTY_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Box>
        <Box>
          <Text fontSize="xs" color="fg.muted" mb={1}>
            {text('devices.filters.transport', 'Transport')}
          </Text>
          <NativeSelect.Root size="sm" w="full">
            <NativeSelect.Field
              value={value.transport ?? ''}
              onChange={(e) =>
                update({ transport: e.target.value || undefined })
              }
            >
              {TRANSPORT_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Box>
        <Box>
          <Text fontSize="xs" color="fg.muted" mb={1}>
            {text('devices.filters.jitter', 'Jitter')}
          </Text>
          <NativeSelect.Root size="sm" w="full">
            <NativeSelect.Field
              value={value.jitter ?? ''}
              onChange={(e) => update({ jitter: e.target.value || undefined })}
            >
              {JITTER_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Box>
        <Box>
          <Text fontSize="xs" color="fg.muted" mb={1}>
            {text('devices.filters.sidecar', 'Sidecar')}
          </Text>
          <NativeSelect.Root size="sm" w="full">
            <NativeSelect.Field
              value={
                value.sidecar_enabled === undefined
                  ? ''
                  : String(value.sidecar_enabled)
              }
              onChange={(e) => {
                const v = e.target.value
                update({
                  sidecar_enabled: v === '' ? undefined : v === 'true',
                })
              }}
            >
              {SIDECAR_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Box>
        <Box>
          <Text fontSize="xs" color="fg.muted" mb={1}>
            {text('devices.filters.provisioning', 'Provisioning')}
          </Text>
          <NativeSelect.Root size="sm" w="full">
            <NativeSelect.Field
              value={value.provisioning ?? ''}
              onChange={(e) =>
                update({ provisioning: e.target.value || undefined })
              }
            >
              {PROVISIONING_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Box>
      </VStack>
    )
  },
)

DevicesListFilters.displayName = 'DevicesListFilters'
